import { fmtClaim, type GbClaim, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// Nomination — declare a value for each of your hidden balls
// ---------------------------------------------------------------------------
// You declare ON THE BOARD: the hidden balls in your own seat panel are
// the selection surface, so your hand appears exactly once. This sheet
// is only the value picker for whichever ball is selected.
//
// The picker only ever offers the server's legal set, so an illegal
// claim is unreachable rather than merely rejected — and it shows ALL of
// them at once. A scrolling strip that revealed four of twenty-one made
// most of the legal claims effectively unreachable.

export function NominationSheet({
  seat,
  selectedBallId,
  legalValues,
  busy,
  onClaim
}: {
  seat: GbSeatView;
  /** Chosen in the seat panel above. */
  selectedBallId: string | null;
  legalValues: GbClaim[];
  busy: boolean;
  onClaim: (ballId: string, value: GbClaim) => void;
}) {
  const selectedBall = seat.backRow.find((ball) => ball.ballId === selectedBallId);
  const index = seat.backRow.findIndex((ball) => ball.ballId === selectedBallId);
  const allDeclared = seat.backRow.every((ball) => ball.committed);

  // Sizes to its CONTENT and shrinks only when it has to (flex: 0 1
  // auto). Giving it flex-[2] made it claim two thirds of the column
  // whatever its height, which is what left a large void under a
  // two-row picker on a desktop while the table above it scrolled.
  return (
    <div className="flex min-h-0 flex-initial flex-col gap-1.5">
      <header className="flex shrink-0 flex-wrap items-baseline gap-x-2">
        <h3 className="font-display text-xs font-semibold uppercase tracking-wide text-ec-face">
          {selectedBall
            ? `Claim for hidden ball ${index + 1}`
            : 'Pick one of your hidden balls'}
        </h3>
        {selectedBall && (
          <span className="font-serif text-[0.65rem] text-ec-bone-dim">
            really {fmtClaim(selectedBall.yourTruth)} ·{' '}
            {selectedBall.claim === null
              ? 'not declared yet'
              : `you say ${fmtClaim(selectedBall.claim)}`}
          </span>
        )}
        {allDeclared && (
          <span className="ml-auto font-display text-[0.55rem] uppercase tracking-wide text-ec-go">
            all declared — still changeable
          </span>
        )}
      </header>

      {/* EVERY legal claim, visible at once. Auto-fit columns so a phone
          shows the whole set without a scroll strip hiding most of it;
          the container scrolls only if the reserve is genuinely huge. */}
      <div className="min-h-[5rem] flex-initial overflow-y-auto rounded-lg border border-ec-line bg-ec-panel p-1.5">
        {/* Denser on a phone: at 4.25rem only four columns fitted 360px,
            so twenty-one values needed six rows and most of them were
            off screen under the board. ~3.4rem gives five columns and
            four rows. */}
        <div
          className="grid gap-1 [grid-template-columns:repeat(auto-fit,minmax(3.4rem,1fr))] sm:[grid-template-columns:repeat(auto-fit,minmax(4.25rem,1fr))]"
        >
          {legalValues.map((value) => {
            const isCurrent = selectedBall?.claim === value;
            return (
              <button
                key={String(value)}
                type="button"
                disabled={busy || !selectedBallId}
                onClick={() => selectedBallId && onClaim(selectedBallId, value)}
                className={[
                  'min-h-[2.25rem] rounded border px-0.5 py-0.5 font-serif text-[0.65rem] tabular-nums transition sm:min-h-[2.5rem] sm:text-[0.7rem]',
                  value === 'KILLER'
                    ? 'border-ec-oxblood-2/60 text-ec-oxblood-2'
                    : 'border-ec-line text-ec-face',
                  isCurrent ? 'bg-ec-brass/20 ring-1 ring-ec-brass' : 'hover:border-ec-brass/60',
                  'disabled:opacity-40'
                ].join(' ')}
              >
                {fmtClaim(value)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
